import { capabilityDefinition, CAPABILITY_KEYS, } from "../capabilities/registry.js";
/**
 * **قوالبُ الأدوار — يبدأ بها كلُّ تنصيب، ويغيّرها صاحبُه.** البلوبرنت §2.1، قرار شادي.
 *
 * وكانت في البذرة وحدها، فلا تصل مشترياً ولا TWING، **وفيها ما صدم شادي في Planner**: مديرُ نظامٍ
 * بلا طابور نشر، ومودريتور بلا إثبات نشر، ودورٌ ثانٍ لنفس الوظيفة. **فالقوالبُ هنا موضعٌ واحد** يقرؤه
 * معالجُ الإعداد والبذرة وأمرُ الوكالات.
 *
 * **والقاعدة:** كلُّ موظفٍ يرى الخططَ والكروتَ والطابورَ ويناقش فيها، **وصاحبُ الشغل وحده يتصرّف**.
 * ولا يراه إلا المسؤولون: تقريرُ الشهر، والمهامُّ المباشرة الموجَّهة لشخصٍ أو قسم.
 */
const COMPANY = { type: "company" };
const OWN = { type: "own-record" };
/** **قسمُه هو** — يُشتقّ من رئاسته وقت التقييم، لا يُملأ عند الإسناد */
const DEPARTMENT = { type: "department", of: "headed" };
const ASSIGNEE = { type: "assignee" };
/** **عملاء فريقه** — يُشتقّ من عضوية فريق العميل وقت التقييم */
const CLIENT = { type: "assigned-client" };
const CREATOR = { type: "creator" };
/** **عميلٌ يديره** — يُشتقّ من وسم مدير الحساب */
const MANAGED = { type: "managed-client" };
const grant = (capability, scope) => ({ capability, scope });
const all = (scope, ...capabilities) => capabilities.map((capability) => grant(capability, scope));
/**
 * **داخليّةٌ أم للبورتال؟ — من الإعلان، لا من بادئة الاسم.**
 *
 * وفحصُ البادئة هو ما أسقط قدرةً في v2 فبقيت شاشةُ البورتال فارغة. ومستخرَجةٌ لتُختبَر وحدها.
 */
export function isInternal(definition) {
    return definition.portalContact !== true;
}
function widestAllowed(capability) {
    const allowed = capabilityDefinition(capability).allowedScopes;
    if (allowed.includes("company"))
        return COMPANY;
    if (allowed.includes("own-record"))
        return OWN;
    if (allowed.includes("department"))
        return { type: "department", of: "member" };
    const first = allowed[0];
    if (first === undefined)
        throw new Error(`capability allows no scope: ${capability}`);
    return { type: first };
}
/**
 * دورُ المالك — **بنطاقٍ تُجيزه كل قدرة، لا بنطاق الشركة عمياء**.
 *
 * فقدرةٌ لا تُجيز إلا «سجلّه هو» تُمنَح به. وهذا بالضبط ما أخطأه v2: منح الجميع نطاق الشركة، فكان
 * الدور المُنشأ مرفوضاً من مدقّق مصفوفته نفسه. **ولا قدرة بورتال في دورٍ داخليّ.**
 */
export function ownerMatrix() {
    return CAPABILITY_KEYS.filter((key) => isInternal(capabilityDefinition(key))).map((capability) => grant(capability, widestAllowed(capability)));
}
/**
 * **ما لا يملكه مديرُ النظام — المسحُ وحده** (قرار شادي): يمشّي الشغل كلَّه في غياب المالك، ولا يمسح
 * عميلاً ولا بياناته ولا موظفاً ولا خطةً ولا ميديا غيره، فلا يقع شيءٌ بالغلط. وإزالةُ تعليقٍ مسموحة
 * له بنصّه. **وسقفُ المنح يمنعه من منح ما لا يحمل**، لأحدٍ ولا لنفسه.
 */
export const SYSTEM_MANAGER_EXCLUDES = [
    "identity.users.delete",
    "clients.delete",
    "platform.audit.purge",
    "plans.delete",
    "files.remove_others",
    /** **وبعين الموظف للمالك وحده** (قرار شادي) */
    "identity.view_as",
];
/** **ما يحمله كلُّ موظف** — يرى كلَّ الشغل ويناقشه، ويتصرّف في شغله هو */
const STAFF = [
    ...all(COMPANY, "clients.view", "boards.view", "cards.view", "plans.view", "moderation.queue.view", "cards.comment", "plans.comment", 
    /** **ويفتح كارتاً لأيّ موظف** — حتى الإدارة، ولا يضيف عميلاً أبداً (قرار شادي) */
    "cards.create", "analytics.ranking.view", "chat.general.view", "chat.direct.start", "chat.group.create", "chat.send"),
    ...all(ASSIGNEE, "cards.update", "cards.change_state", "cards.attach", "cards.due.request", "plans.items.update", "plans.items.transition", "plans.tasks.deliver", "plans.media.manage", "tasks.view", "tasks.change_state", "tasks.comment", "tasks.attach"),
    ...all(OWN, "analytics.own.view", "identity.profile.manage_own", "identity.sessions.revoke_own", "identity.push.manage_own", "chat.edit_own", "notifications.view_own", "actions.view_own", "actions.defer_own", "access_requests.create"),
    /** **وقناةُ العميل الداخلية لفريقه** — فهي حيث يُنسَّق شغلُه، وليست خيطَ العميل */
    grant("chat.client.view", CLIENT),
];
/** **سلطةُ قسمه** — فوق الموظف، وتعلو نطاقَ «المكلَّف» في شغل فريقه */
const HEAD = all(DEPARTMENT, "access_requests.resolve", 
/**
 * **يرى قسمَه، ولا يدير الحسابات** — فإضافةُ موظفٍ وإيقافُه وتغييرُ دوره للمالك ومدير النظام:
 * ليست في جدول §2.1، وكان رئيسُ القسم يفتح ملفَّ موظفٍ فتظهر له رسالةُ رفض.
 */
"identity.users.view", "org.departments.view", "analytics.department.view", "analytics.export", "reports.view", "reports.create", "reports.update", "reports.review_head", "reports.exception.request", "reports.comment", "reports.attach", "tasks.view", "tasks.create", "tasks.update", "tasks.change_state", "tasks.comment", "tasks.attach", "tasks.comments.moderate", "clients.team.manage", "boards.manage", "cards.update", "cards.change_state", "cards.comments.moderate", "cards.attach", "cards.labels.manage", "cards.archive", "cards.due.request", "cards.due.change", "cards.escalations.receive", "plans.manage", "plans.delete", "plans.overview", "plans.items.create", "plans.items.update", "plans.items.assign", "plans.items.transition", "plans.tasks.deliver", "plans.items.review", "plans.items.cancel", "plans.items.visibility", "plans.items.withdraw", "plans.media.manage", "plans.comments.moderate", "files.remove_others", "chat.client.view");
/** **إثباتُ النشر** — المودريتور على كل العملاء (قرار شادي) */
const PUBLISHER = all(COMPANY, "moderation.schedule", "moderation.publish");
/** **تقريرُ عملائه** — يكتب، ويعدّل ما كتبه هو، ولا يراجع */
const REPORT_WRITER = [
    ...all(CLIENT, "reports.view", "reports.create", "reports.comment", "reports.attach"),
    ...all(CREATOR, "reports.update", "reports.exception.request"),
];
/**
 * **الأحدثُ يعلو الأقدم على القدرة نفسها** — فلكل دورٍ نطاقٌ واحدٌ لكل قدرة (`role_permissions`)،
 * وقسمُ الرئيس يحلّ محلّ «المكلَّف»، لا يُضاف بجانبه.
 */
function layered(...layers) {
    const out = new Map();
    for (const layer of layers) {
        for (const one of layer)
            out.set(one.capability, one);
    }
    return [...out.values()];
}
/** **كلُّ قالبٍ يُبنى عند طلبه** — فقدرةٌ تُضاف تدخل المالكَ من يومها، بلا تعديلٍ هنا */
export const ROLE_TEMPLATES = [
    { key: "owner", nameAr: "المالك", nameEn: "Owner", grants: ownerMatrix },
    {
        key: "system-manager",
        nameAr: "مدير النظام",
        nameEn: "System Manager",
        grants: () => ownerMatrix().filter((one) => !SYSTEM_MANAGER_EXCLUDES.includes(one.capability)),
    },
    {
        key: "department-head",
        nameAr: "رئيس قسم",
        nameEn: "Department Head",
        grants: () => layered(STAFF, HEAD),
    },
    {
        key: "moderation-head",
        nameAr: "رئيس قسم المودريشن",
        nameEn: "Moderation Head",
        /** **رئيسُ قسمٍ كامل** — ومعه سحبُ إثبات النشر وإقفالُ طلب الإزالة، بدوره لا باسم قسمه في الكود */
        grants: () => layered(STAFF, HEAD, PUBLISHER, all(COMPANY, "moderation.unpublish", "moderation.takedown.resolve")),
    },
    {
        key: "moderator",
        nameAr: "مودريتور",
        nameEn: "Moderator",
        grants: () => layered(STAFF, PUBLISHER),
    },
    {
        key: "media-buyer",
        nameAr: "ميديا باير",
        nameEn: "Media Buyer",
        grants: () => layered(STAFF, REPORT_WRITER),
    },
    {
        key: "account-manager",
        nameAr: "مدير حساب",
        nameEn: "Account Manager",
        /** **يملك العلاقة** — خططُ عملائه وإسنادُها، وشاتُهم واجتماعاتُهم وجهاتُ اتصالهم وتقاريرُهم */
        grants: () => layered(STAFF, REPORT_WRITER, all(CLIENT, "plans.items.create", "plans.items.assign", "clients.portal.manage"), all(MANAGED, "chat.portal.respond", "meetings.view", "meetings.decide", "meetings.comment", 
        /** **ويبتّ في موعد كروت عملائه** (قرار شادي): بنطاقه، لا باستثناءٍ في الكود */
        "cards.due.change")),
    },
    { key: "graphic-designer", nameAr: "مصمم", nameEn: "Designer", grants: () => STAFF },
    { key: "video-editor", nameAr: "مونتير", nameEn: "Video Editor", grants: () => STAFF },
    { key: "content-creator", nameAr: "كاتب محتوى", nameEn: "Content Writer", grants: () => STAFF },
];
const ASSIGNED_CLIENT = { type: "assigned-client" };
/**
 * **دورُ جهة اتصال العميل — يصل كل تنصيب، لا البذرةَ وحدها.**
 *
 * كان في بذرة الديمو والاختبار فقط: فعلى TWING وأيّ مشترٍ **لا دورَ لجهة الاتصال**، فلا
 * يُضاف عميلٌ أصلاً، ومن نُقل حسابُه يدخل فترفضه كلُّ شاشةٍ في البورتال. مرّ على كل
 * الفحوص لأن الاختباراتِ تزرعه بنفسها. **وجده الدخولُ بحسابات عملاء حقيقية.**
 *
 * وليس بين `ROLE_TEMPLATES`: فتلك أدوارُ الموظفين، وهذا لا يحمل قدرةً داخلية أبداً.
 */
export const PORTAL_CONTACT_TEMPLATE = {
    key: "portal-contact",
    nameAr: "جهة اتصال العميل",
    nameEn: "Client contact",
    grants: () => [
        ...all(ASSIGNED_CLIENT, "portal.plans.view", "portal.items.decide", "portal.items.request_edit", "portal.items.comment", "portal.reports.request_edit", "portal.schedule.view", "portal.chat.send", "portal.meetings.request", "portal.contacts.view"),
        ...all(OWN, "portal.push.manage", "notifications.view_own"),
    ],
};
export function roleTemplate(key) {
    const found = ROLE_TEMPLATES.find((one) => one.key === key);
    if (!found)
        throw new Error(`no role template: ${key}`);
    return found;
}
/**
 * **من ينشئ مهمةً يبقى يراها** — ولو وجّهها لقسمٍ لا يبلغه.
 *
 * فرئيسُ القسم يوجّه مهمةً للإدارة (قرار شادي)، وقسمُها خارج نطاقه: فبلا هذا **ترسل مهمةً فتختفي من
 * أمامك**. ولكل دورٍ نطاقٌ واحدٌ لكل قدرة، فيُضاف عند حساب اللقطة، موضعاً واحداً.
 */
export const IMPLIED_BY_CREATING = [
    { when: "tasks.create", then: "tasks.view", scope: CREATOR },
    { when: "tasks.create", then: "tasks.comment", scope: CREATOR },
    { when: "tasks.create", then: "tasks.attach", scope: CREATOR },
];
//# sourceMappingURL=role-templates.js.map